import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPalette, FaCheck } from 'react-icons/fa';
import { colorSchemes, applyWebsiteTheme, getCurrentWebsiteTheme } from '../../utils/updateWebsiteTheme';

const ColorScheme = () => {
  const [selectedScheme, setSelectedScheme] = useState(null);
  const [isApplying, setIsApplying] = useState(false);

  useEffect(() => {
    // Load current theme
    const currentTheme = getCurrentWebsiteTheme();
    setSelectedScheme(currentTheme);
  }, []);

  const handleSchemeSelect = async (scheme) => {
    setIsApplying(true);
    setSelectedScheme(scheme);
    
    // Apply theme to website
    applyWebsiteTheme(scheme);
    
    // Small delay for visual feedback
    setTimeout(() => {
      setIsApplying(false);
    }, 500);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-[#1a1a3e] rounded-xl p-6 border border-gray-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
            <FaPalette className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Website Color Scheme</h1>
            <p className="text-gray-400">Customize the public website colors</p>
          </div>
        </div>
        
        <div className="bg-[#0a0a1f] rounded-lg p-4 border border-gray-800">
          <p className="text-yellow-400 text-sm">
            <strong>Note:</strong> Color changes only affect the public website. 
            The admin panel will keep its current design.
          </p>
        </div>
      </div>

      {/* Current Theme Display */}
      {selectedScheme && (
        <div className="bg-[#1a1a3e] rounded-xl p-6 border border-gray-700">
          <h2 className="text-lg font-semibold text-white mb-4">Current Theme</h2>
          <div className="flex items-center gap-4 p-4 bg-[#0a0a1f] rounded-lg border border-gray-800">
            <div className="flex gap-2">
              <div 
                className="w-8 h-8 rounded border-2 border-gray-600"
                style={{ backgroundColor: selectedScheme.primary }}
                title="Primary"
              />
              <div 
                className="w-8 h-8 rounded border-2 border-gray-600"
                style={{ backgroundColor: selectedScheme.secondary }}
                title="Secondary"
              />
              <div 
                className="w-8 h-8 rounded border-2 border-gray-600"
                style={{ backgroundColor: selectedScheme.accent }}
                title="Accent"
              />
              <div 
                className="w-8 h-8 rounded border-2 border-gray-600"
                style={{ backgroundColor: selectedScheme.hover }}
                title="Hover"
              />
            </div>
            <div>
              <p className="text-white font-medium">{selectedScheme.name}</p>
              <p className="text-gray-400 text-sm">Active on public website</p>
              <div className="text-xs text-gray-500 mt-1">
                Professional • Strong Colors • Full Coverage
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Color Scheme Grid */}
      <div className="bg-[#1a1a3e] rounded-xl p-6 border border-gray-700">
        <h2 className="text-lg font-semibold text-white mb-6">Available Themes</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {colorSchemes.map((scheme) => {
            const isSelected = selectedScheme?.id === scheme.id;
            
            return (
              <motion.div
                key={scheme.id}
                className={`
                  relative p-4 rounded-lg border-2 cursor-pointer transition-all
                  ${isSelected 
                    ? 'border-purple-500 bg-[#0a0a1f]' 
                    : 'border-gray-700 bg-[#0a0a1f] hover:border-gray-600'
                  }
                `}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleSchemeSelect(scheme)}
              >
                {/* Selected Indicator */}
                {isSelected && (
                  <div className="absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center">
                    <FaCheck className="text-white" size={12} />
                  </div>
                )}

                {/* Color Preview */}
                <div className="flex gap-1 mb-3">
                  <div 
                    className="w-6 h-6 rounded border border-gray-600"
                    style={{ backgroundColor: scheme.primary }}
                    title="Primary"
                  />
                  <div 
                    className="w-6 h-6 rounded border border-gray-600"
                    style={{ backgroundColor: scheme.secondary }}
                    title="Secondary"
                  />
                  <div 
                    className="w-6 h-6 rounded border border-gray-600"
                    style={{ backgroundColor: scheme.accent }}
                    title="Accent"
                  />
                  <div 
                    className="w-6 h-6 rounded border border-gray-600"
                    style={{ backgroundColor: scheme.hover }}
                    title="Hover"
                  />
                </div>

                {/* Theme Info */}
                <h3 className="text-white font-medium mb-2">{scheme.name}</h3>
                <div className="text-xs text-gray-400 space-y-1">
                  <div className="flex justify-between">
                    <span>Primary:</span>
                    <span className="font-mono">{scheme.primary}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Background:</span>
                    <span className="font-mono">{scheme.background}</span>
                  </div>
                </div>

                {/* Preview Bar */}
                <div 
                  className="mt-3 h-3 rounded-full"
                  style={{ 
                    background: `linear-gradient(135deg, ${scheme.primary}, ${scheme.secondary}, ${scheme.accent})` 
                  }}
                />
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Apply Status */}
      {isApplying && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-green-500/20 border border-green-500 rounded-lg p-4"
        >
          <p className="text-green-400 text-center">
            ✅ Theme applied to public website successfully!
          </p>
        </motion.div>
      )}

      {/* Instructions */}
      <div className="bg-[#1a1a3e] rounded-xl p-6 border border-gray-700">
        <h3 className="text-white font-semibold mb-3">Professional Color System</h3>
        <ul className="text-gray-400 space-y-2 text-sm">
          <li>• <strong className="text-green-400">12 Professional Themes</strong> - Strong, readable colors</li>
          <li>• <strong className="text-blue-400">Full Website Coverage</strong> - Header, footer, buttons, cards, text</li>
          <li>• <strong className="text-purple-400">Instant Application</strong> - Changes apply immediately</li>
          <li>• <strong className="text-yellow-400">Admin Panel Protected</strong> - Only public website changes</li>
          <li>• <strong className="text-pink-400">Persistent Storage</strong> - Themes saved across sessions</li>
        </ul>
        
        <div className="mt-4 p-3 bg-[#0a0a1f] rounded-lg border border-gray-800">
          <p className="text-xs text-gray-500">
            <strong>Preview:</strong> Open the public website in a new tab to see theme changes in real-time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ColorScheme;